import React from 'react';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { useEmpaques } from '../../hooks/useParametric';
import { type Empaque } from '../../types/catalog';

export interface EmpaqueSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Whether to show external empaques (esExterno = true) or internal empaques (esInterno = true) */
  isExterno: boolean;
  /** Custom filter function to filter empaques */
  filter?: (empaque: Empaque) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * EmpaqueSelect Component
 *
 * A reusable dropdown component that displays empaques from parametric data.
 * Features:
 * - Automatic data loading from parametric store
 * - Loading states and error handling
 * - Filtering by external/internal type based on isExterno prop
 * - Additional custom filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const EmpaqueSelect: React.FC<EmpaqueSelectProps> = ({
  value,
  onChange,
  label = 'Empaque',
  error,
  disabled = false,
  required = false,
  placeholder,
  isExterno,
  filter,
  sx,
}) => {
  const { data: empaques, loading, fetch: fetchEmpaques } = useEmpaques();

  // Fetch empaques on component mount
  React.useEffect(() => {
    fetchEmpaques();
  }, [fetchEmpaques]);

  // Filter empaques based on props
  const filteredEmpaques = React.useMemo(() => {
    let filtered = empaques;

    // Filter by external/internal type
    if (isExterno) {
      filtered = filtered.filter((empaque) => empaque.esExterno === true);
    } else {
      filtered = filtered.filter((empaque) => empaque.esInterno === true);
    }

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [empaques, isExterno, filter]);

  // Convert empaques to select options
  const options = React.useMemo(() => {
    return filteredEmpaques.map((empaque) => ({
      value: empaque.idEmpaque,
      label: empaque.descripcion,
    }));
  }, [filteredEmpaques]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default EmpaqueSelect;